import React from "react";
import { useSelector } from "react-redux";
import { FormControlLabel, Switch } from "@mui/material";
import moment from "moment";
import Swal from "sweetalert2";
import { enqueueSnackbar } from "notistack";
import { CREATE_OUT_OF_OFFICE_DAYS, DELETE_OUT_OF_OFFICE_DAYS } from "lib/constants";
import { postAPICall, deleteAPICall } from "lib/callApi";

function OutOfOfficeSwitch({
  isOutOfOfficeDay = false,
  selectedDate,
  setIsLoading,
  handleCreateOutOfOfficeDaysSuccess,
  handleDeleteOutOfOfficeDaysSuccess,
  alertTarget,
}) {
  const user = useSelector((state) => state.loginUser.loginUserState);

  const createOutOfOfficeDays = async () => {
    try {
      setIsLoading?.(true);
      const payload = {
        startDate: moment(selectedDate).startOf("d").format("YYYY-MM-DD"),
        endDate: moment(selectedDate).endOf("d").format("YYYY-MM-DD"),
      };

      const result = await postAPICall(CREATE_OUT_OF_OFFICE_DAYS, true, payload);

      if (result?.data?.success) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "success",
            title: "Success",
            text: result.data.message,
          }),
          {
            variant: "custom",
          }
        );

        handleCreateOutOfOfficeDaysSuccess?.();
      } else {
        const responseMessage = result?.data?.message || result?.response?.data?.message;
        enqueueSnackbar(
          JSON.stringify({
            icon: "info",
            title: "Information",
            text: responseMessage,
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (err) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "info",
          title: "Information",
          text: err.message,
        }),
        {
          variant: "custom",
        }
      );
    } finally {
      setIsLoading?.(false);
    }
  };

  const deleteOutOfOfficeDays = async () => {
    setIsLoading?.(true);
    try {
      const params = new URLSearchParams();
      params.append("days", moment(selectedDate).startOf("d").format("YYYY-MM-DD"));
      params.set("userCompanyLinkId", user.userCompanyLinkId);

      const deletedCompanyHolidays = await deleteAPICall(DELETE_OUT_OF_OFFICE_DAYS, true, params);
      if (deletedCompanyHolidays?.data?.success) {
        handleDeleteOutOfOfficeDaysSuccess?.();
      } else if (deletedCompanyHolidays.response.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: deletedCompanyHolidays.response.data.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (err) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: err.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    } finally {
      setIsLoading?.(false);
    }
  };

  const handleOutOfOfficeChange = async (e) => {
    const checked = !!e.target.checked;

    await Swal.fire({
      ...(alertTarget ? { target: alertTarget } : {}),
      html: `<h2>${
        checked
          ? "Set this day as an Out-of-Office day?"
          : "Clear this day from being marked as Out-of-Office?"
      }</h2>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      reverseButtons: true,
    }).then(async ({ isConfirmed = false }) => {
      if (isConfirmed) {
        if (checked) createOutOfOfficeDays();
        else deleteOutOfOfficeDays();
      }
    });
  };

  return (
    <FormControlLabel
      value="start"
      control={<Switch checked={isOutOfOfficeDay || false} onChange={handleOutOfOfficeChange} />}
      label="Out of Office"
    />
  );
}

export default OutOfOfficeSwitch;
